import React, { useState, useEffect } from 'react'

import { toast } from 'react-toastify'

import { useHistory } from 'react-router-dom'

import HomeContractor from '../../../../../presentation/pages/contractors/home'

import { useFirstAccess } from '../../../../../presentation/hooks/first-access'
import { useFetchContracts } from '../../../../../presentation/hooks/fetch-contracts'
import { useResource } from '../../../../../presentation/hooks/resource'
import { useFetchResources } from '../../../../../presentation/hooks/fetch-resources'
import { useMe } from '../../../../../presentation/contexts/me'
import useNotificationPolling from '../../../../../presentation/hooks/notification-polling'
import useFetchRanking from '../../../../../presentation/hooks/fetch-ranking'
import useNewMessageNotification from '../../../../../presentation/hooks/new-message-notification'
import useFetchConversations from '../../../../../presentation/hooks/fetch-conversations'

import { makeSpecialties } from '../../../../usecases/specialties/remote-specialties-factory'
import { makeContractorsContracts } from '../../../../usecases/contracts/remote-contracts-factory'
import { makeRequests } from '../../../../usecases/requests/remote-requests-factory'
import { makeContractors } from '../../../../usecases/contractors/remote-contractors-factory'
import { makeStates } from '../../../../usecases/states/remote-states-factory'
import { makeCities } from '../../../../usecases/cities/remote-cities-factory'

import IContractedDoctor from '../../../../../presentation/interfaces/contracted-doctor'
import ISpecialty from '../../../../../presentation/interfaces/specialty'
import IState from '../../../../../presentation/interfaces/state'
import IFile from '../../../../../presentation/interfaces/file'

import { Authority, RoutesEnum } from '../../../../../presentation/enums'
import {
  getRoute,
  isHttpError,
  HelperNotificationToken,
} from '../../../../../presentation/helpers'

const MakeHomeContractor = () => {
  document.title = 'BuscarMédicos - Início'

  const [requests, setRequests] = useState<any[]>([])

  const [files, setFiles] = useState<IFile[]>([])

  const { me } = useMe()

  const history = useHistory()

  const [specialties, specialtiesService] = useResource<ISpecialty>(
    makeSpecialties(),
  )

  const [states, setStates] = useState<IState[]>([])

  const { isModalOpen, handleModalClose } = useFirstAccess()

  const { contracts } = useFetchContracts<IContractedDoctor>(
    makeContractorsContracts(me?.id as number),
  )

  const hasNewNotification = useNotificationPolling()

  const hasNewMessage = useNewMessageNotification(me?.id)

  useFetchResources(specialtiesService.load)

  const conversations = useFetchConversations(me?.id)

  useEffect(() => {
    const fetchData = async () => {
      if (me) {
        try {
          const data = await makeRequests(
            `/solicitations/${me.id}/contractors`,
          ).load()

          setRequests(data)
        } catch (error) {
          const httpError = isHttpError(error)
          if (httpError) {
            toast.error(`Erro ao buscar as solicitações: ${httpError.message}`)
          } else {
            throw error
          }
        }
      }
    }

    fetchData()
  }, [me])

  useEffect(() => {
    const loadFiles = async () => {
      if (me) {
        try {
          const data = await makeContractors().loadById(me.id)
          setFiles(data.document)
        } catch (error) {
          const httpError = isHttpError(error)
          if (httpError) {
            toast.error(`Erro ao buscar os arquivos: ${httpError.message}`)
          } else {
            throw error
          }
        }
      }
    }

    loadFiles()
  }, [me])

  const rankingData = useFetchRanking()

  useEffect(() => {
    const requestPermission = async () => {
      await HelperNotificationToken.requestPermission()
    }

    requestPermission()
  }, [])

  useEffect(() => {
    const fetchStates = async () => {
      try {
        const data = await makeStates().load()
        const formattedData = data.map((state: any) => ({
          ...state,
          siglaIsoIntegration: state.siglaIsoIntegration.slice(3, 5),
        }))

        setStates(formattedData)
      } catch (error) {
        const httpError = isHttpError(error)
        if (httpError) {
          toast.error(`Erro ao buscar os estados: ${httpError.message}`)
        } else {
          throw error
        }
      }
    }

    fetchStates()
  }, [])

  const handleSearch = () => {
    history.push(RoutesEnum.RESULT_CONTRACTOR)
  }

  const loadCities = async (stateId: number) => {
    const cities = makeCities()
    return cities.loadById(stateId)
  }

  const recentContacts = requests
    .map((doctor) => ({
      ...doctor,
      linkChat: getRoute(RoutesEnum.CHAT, {
        interlocutorType: Authority.DOCTOR,
        interlocutorId: String(doctor.doctorId),
      }),
      linkProfile: getRoute(RoutesEnum.DOCTOR_CONTRACTOR, {
        id: String(doctor.doctorId),
      }),
      hasNewMessage: conversations.some(
        (conversation) => conversation.id === String(doctor.doctorId)
          && conversation.messages.some(
            (message) => !message.isRead && message.receiverId === String(me?.id),
          ),
      ),
    }))
    .sort((a, b) => Number(b.createdAt) - Number(a.createdAt))
    .slice(0, 5)

  const contractedDoctors = contracts
    .map((doctor) => ({
      ...doctor,
      linkProfile: getRoute(RoutesEnum.DOCTOR_CONTRACTOR, {
        id: String(doctor.doctorId),
      }),
    }))
    .sort((a, b) => Number(b.createdAt) - Number(a.createdAt))
    .slice(0, 5)

  if (!me) return null

  const homeContractorProps = {
    user: {
      ...me,
      imgUrl: files.find((file) => file.url.startsWith('images/'))?.url,
    },
    recentContacts,
    contractedDoctors,
    handleSearch,
    isModalOpen,
    handleModalClose,
    hasNewNotification,
    hasNewMessage,
    specialties,
    rankingData,
    states,
    loadCities,
  }

  return <HomeContractor {...homeContractorProps} />
}

export default MakeHomeContractor
